import {v4 as uuid} from 'uuid';

import * as validation from '../../lib/validation';
import {buildCatalogTree, buildNameListFromTreeForAnswers} from '../../lib/catalog';

export function createQuestionOption(value, order) {
  return {value, order};
}

export function getLogicBuilderVariables(form, catalog, question = null, options = {}) {
  const nameOptions = buildNameListFromTreeForAnswers('', '', buildCatalogTree(catalog))
    .map(item => ({name: item.name, value: item.id}));

  form.questions.sort(function(a,b) {return a.order - b.order});

  return [...form.contextQuestions, ...form.questions]
    .filter(q => question ? q.id !== question.id : true)
    .filter(q => q.type !== 'IMAGE' && q.type !== 'SIGNATURE')
    .filter(q => options.excludeNumericGrids ? q.type !== 'NUMERIC_GRID' : true)
    .filter(q => options.excludeMultiValue ? (q.type !== 'NUMERIC_GRID' && q.type !== 'MULTI_NUMERIC') : true)
    .reduce((map, question) => {
      let questionOptions = question.questionOptions;

      if (question.type === 'CATALOG_RADIO' || question.type === 'CATALOG_CHECKBOX') {
        questionOptions = nameOptions;
      }

      map[question.id] = {
        label: question.text,
        type: question.type,
        isMultiValue: question.type === 'MULTI_NUMERIC' || question.type === 'NUMERIC_GRID',
        options: questionOptions.map(option => ({
          name: option.name ? option.name : option.value,
          value: option.value
        }))
      };

      return map;
    }, {});
}

const isValuePresent = validation.create((value, {isEmpty, trim}) => !isEmpty(trim(value)));

function isValidJsonLogic(expression) {
  try {
    return JSON.parse(expression) !== null;
  }
  catch (e) {
    return false;
  }
}

export function getValidationErrors(form, options = {disabled: false}) {
  return {
    name: isValuePresent(form.name) || options.disabled ? null : 'A form name is required',
    questions: form.questions.some(question => question.required) || options.disabled ? null : 'At least one repeated question must be required',
    questionsById: [...form.contextQuestions, ...form.questions].reduce((map, question) => {
      map[question.id] = {
        text: isValuePresent(question.text) || options.disabled ? null : 'A question title is required',
        type: isValuePresent(question.type) || options.disabled ? null : 'A type is required',
        defaultAnswers: null,
        questionOptionsByValue: hasChoices(question.type) ? question.questionOptions.reduce((map, option) => {
          map[option.value] = null;
          
          if (!options.disabled) {
            if (!isValuePresent(option.value)) {
              map[option.value] = 'An option value is required';
            }
            else if (question.questionOptions.filter(o => o.value === option.value).length > 1) {
              map[option.value] = 'Duplicate option';
            }
          }
          
          return map;
        }, {}) : {}
      };

      return map;
    }, {}),
    alertsById: form.alerts.reduce((map, alert) => {
      map[alert.id] = {
        name: isValuePresent(alert.name) || options.disabled ? null : 'An alert name is required',
        expression: isValidJsonLogic(alert.expression) || options.disabled ? null : 'A condition to trigger this alert is required'
      };

      return map;
    }, {}),
    contextAlertsById: form.contextAlerts.reduce((map, contextAlert) => {
      map[contextAlert.id] = {
        name: isValuePresent(contextAlert.name) || options.disabled ? null : 'A contextAlert name is required',
        formId: isValuePresent(contextAlert.formId) || options.disabled ? null : 'A form to complete is required',
        description: isValuePresent(contextAlert.description) || options.disabled ? null : 'A contextAlert description is required',
        expression: isValidJsonLogic(contextAlert.expression) || options.disabled ? null : 'A condition to trigger this contextAlert is required'
      };

      return map;
    }, {}),
    // get all notificationExpressions
    notificationExpressionErrors: (() => {
      const groups = [
        form.rowCompleteContacts || [],
        form.rowApprovedContacts || [],
        form.rowRejectedContacts || []
      ];
      // flatten to each expression
      const allUserExprs = groups.flat();
      // ensure that userId is not missing
      const nullUserId = allUserExprs.some(expr => {
        return !(expr.userId || (expr.user && expr.user.id));
      });
      // if null User stop user from saving form
      return nullUserId
          ? ["A user must be selected for all notification recipients"]
          : [];
    })(),
    reviewers: form.reviewers.length === 0 && (form.rowApprovedContacts.length > 0 || form.rowRejectedContacts.length > 0)
      ? 'At least one reviewer is required' : null
  }; 
}

export function hasValidationErrors(validations) {
  if (validations === null) {
    return false;
  }

  if (Array.isArray(validations)) {
    return validations.some(validation => hasValidationErrors(validation));
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .some(validation => hasValidationErrors(validation));
  }

  return true;
}

export function flattenValidationErrors(validations) {
  if (validations === null) {
    return [];
  }

  if (Array.isArray(validations)) {
    return validations.reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  return [validations];
}

export function hasChoices(questionType) {
  return questionType === 'RADIO' || questionType === 'CHECKBOXES';
}

export function createQuestion(order) {
  return {
    id: uuid(),
    order,
    context: false,
    text: '',
    type: '',
    defaultAnswers: [],
    required: true,
    questionMeta: [],
    questionOptions: [],
    condition: null
  };
}

export function getQuestionMetadata(question, key) {
  return question.questionMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}